import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Clapperboard, Quote as QuoteIcon } from "lucide-react";
import { PageHeader, Btn, Field, inputCls, Modal, Card } from "@/components/cc";

const SCRIPT_TYPES = ["60-Second Short", "90-Second Short", "3-Minute Explainer", "5-Minute Commentary", "10-Minute Analysis", "News Briefing", "Political Commentary", "Educational Video", "Documentary Script", "Inspirational Video"];
const QCATS = ["Leadership", "Relationships", "Emotional Intelligence", "Politics", "Society", "Africa", "Personal Growth", "Hard Truths", "Motivation", "Wisdom", "Civic Education"];
const QFORMATS = ["Square", "Portrait", "Landscape"];

export function Scripts() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", script_type: "60-Second Short", hook: "", opening: "", main_sections: "", conclusion: "", call_to_action: "" });
  const [outputs, setOutputs] = useState(null);
  const load = () => api.get("/studio/scripts").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const create = async (e) => { e.preventDefault(); try { await api.post("/studio/scripts", form); toast.success("Script created"); setOpen(false); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  const showOut = async (id) => { const { data } = await api.get(`/studio/scripts/${id}/outputs`); setOutputs(data); };

  return (
    <div className="p-6">
      <PageHeader title="Video Script Studio" subtitle="Produce scripts and platform-ready outputs (no AI drafting).">
        <Btn data-testid="new-script-btn" onClick={() => setOpen(true)}><Plus className="h-4 w-4" /> New Script</Btn>
      </PageHeader>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="scripts-list">
        {items.length === 0 && <p className="text-sm text-zinc-600">No scripts yet.</p>}
        {items.map((s) => (
          <Card key={s.id} className="p-4">
            <Clapperboard className="h-4 w-4 text-violet-400" />
            <h3 className="mt-2 font-medium">{s.title}</h3>
            <div className="text-xs text-zinc-500">{s.script_type} · {s.status}</div>
            <Btn variant="ghost" data-testid={`script-outputs-${s.id}`} onClick={() => showOut(s.id)} className="mt-3">View Outputs</Btn>
          </Card>
        ))}
      </div>
      <Modal open={open} onClose={() => setOpen(false)} title="New Video Script" wide>
        <form onSubmit={create} className="space-y-3">
          <Field label="Title"><input data-testid="script-title-input" className={inputCls} required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></Field>
          <Field label="Type"><select className={inputCls} value={form.script_type} onChange={(e) => setForm({ ...form, script_type: e.target.value })}>{SCRIPT_TYPES.map((t) => <option key={t}>{t}</option>)}</select></Field>
          <Field label="Hook"><input className={inputCls} value={form.hook} onChange={(e) => setForm({ ...form, hook: e.target.value })} /></Field>
          <Field label="Main sections (one per line)"><textarea className={inputCls} rows={4} value={form.main_sections} onChange={(e) => setForm({ ...form, main_sections: e.target.value })} /></Field>
          <Field label="Call to action"><input className={inputCls} value={form.call_to_action} onChange={(e) => setForm({ ...form, call_to_action: e.target.value })} /></Field>
          <Btn data-testid="script-save-btn" type="submit" className="w-full justify-center">Create Script</Btn>
        </form>
      </Modal>
      <Modal open={!!outputs} onClose={() => setOutputs(null)} title="Script Outputs" wide>
        {outputs && (
          <div className="space-y-3 text-sm" data-testid="script-outputs">
            {["teleprompter", "youtube_description", "instagram_caption", "tiktok_caption", "hashtags", "thumbnail_text"].map((k) => (
              <div key={k}><div className="label-caps text-zinc-500">{k.replace(/_/g, " ")}</div><pre className="mt-1 bg-[#0E0E0E] border border-zinc-800 rounded-sm p-2 whitespace-pre-wrap text-zinc-300">{typeof outputs[k] === "string" ? outputs[k] : JSON.stringify(outputs[k])}</pre></div>
            ))}
            <div><div className="label-caps text-zinc-500">scene by scene</div><pre className="mt-1 bg-[#0E0E0E] border border-zinc-800 rounded-sm p-2 whitespace-pre-wrap text-zinc-300">{outputs.scene_by_scene.map((s) => `Scene ${s.scene}: ${s.text}`).join("\n")}</pre></div>
          </div>
        )}
      </Modal>
    </div>
  );
}

export function Quotes() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ quote_text: "", author: "James Be-Apuo", category: "Wisdom", format: "Square", short_caption: "", hashtags: "" });
  const load = () => api.get("/studio/quotes").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const create = async (e) => { e.preventDefault(); try { await api.post("/studio/quotes", form); toast.success("Quote created"); setOpen(false); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  const exportQ = async (id) => { const { data } = await api.get(`/studio/quotes/${id}/export`); navigator.clipboard?.writeText(data.canva_block); toast.success("Canva text copied"); };

  return (
    <div className="p-6">
      <PageHeader title="Quote Card Studio" subtitle="Organize and export quote text for Canva (no image generation yet).">
        <Btn data-testid="new-quote-btn" onClick={() => setOpen(true)}><Plus className="h-4 w-4" /> New Quote</Btn>
      </PageHeader>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="quotes-list">
        {items.length === 0 && <p className="text-sm text-zinc-600">No quotes yet.</p>}
        {items.map((q) => (
          <Card key={q.id} className="p-4">
            <QuoteIcon className="h-4 w-4 text-amber-400" />
            <p className="mt-2 font-editorial italic text-zinc-200">"{q.quote_text}"</p>
            <div className="text-xs text-zinc-500 mt-1">{q.category} · {q.format} · {q.status}</div>
            <Btn variant="ghost" data-testid={`quote-export-${q.id}`} onClick={() => exportQ(q.id)} className="mt-3">Copy Canva Text</Btn>
          </Card>
        ))}
      </div>
      <Modal open={open} onClose={() => setOpen(false)} title="New Quote Card">
        <form onSubmit={create} className="space-y-3">
          <Field label="Quote text"><textarea data-testid="quote-text-input" className={inputCls} rows={3} required value={form.quote_text} onChange={(e) => setForm({ ...form, quote_text: e.target.value })} /></Field>
          <Field label="Author"><input className={inputCls} value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Category"><select className={inputCls} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>{QCATS.map((c) => <option key={c}>{c}</option>)}</select></Field>
            <Field label="Format"><select className={inputCls} value={form.format} onChange={(e) => setForm({ ...form, format: e.target.value })}>{QFORMATS.map((f) => <option key={f}>{f}</option>)}</select></Field>
          </div>
          <Btn data-testid="quote-save-btn" type="submit" className="w-full justify-center">Create Quote</Btn>
        </form>
      </Modal>
    </div>
  );
}

export function Subscribers() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/studio/newsletter/subscribers").then((r) => setItems(r.data)).catch(() => {}); }, []);
  return (
    <div className="p-6">
      <PageHeader title="Newsletter Subscribers" subtitle="Consent-based subscriber management (delivery not yet enabled)." />
      <Card>
        <table className="w-full text-sm" data-testid="subscribers-table">
          <thead><tr className="text-left label-caps text-zinc-500 border-b border-zinc-800"><th className="px-4 py-2.5">Email</th><th className="px-4 py-2.5">Status</th><th className="px-4 py-2.5">Newsletters</th><th className="px-4 py-2.5">Suppressed</th></tr></thead>
          <tbody className="divide-y divide-zinc-900">
            {items.length === 0 && <tr><td colSpan={4} className="px-4 py-6 text-zinc-600">No subscribers yet.</td></tr>}
            {items.map((s) => (
              <tr key={s.id} className="hover:bg-zinc-900/50">
                <td className="px-4 py-2.5">{s.email}</td>
                <td className="px-4 py-2.5">{s.status}</td>
                <td className="px-4 py-2.5 text-zinc-400">{(s.newsletters || []).join(", ") || "—"}</td>
                <td className="px-4 py-2.5">{s.suppression ? "Yes" : "No"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
